import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Check,
  AlertTriangle,
  Lock,
  Unlock,
  Trash2,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Plus,
  Factory,
  Truck,
  Loader2,
  Download,
  Calendar,
  Search,
  Link,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function BOMBuilder({ user, onLogout }) {
  const { poId } = useParams();
  const nav = useNavigate();

  // Core state
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [poNumber, setPoNumber] = useState("");
  const [bomType, setBomType] = useState("inhouse");
  const [vendorId, setVendorId] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [dispatchDate, setDispatchDate] = useState("");
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [expandedProducts, setExpandedProducts] = useState({});

  // New vendor dialog
  const [showNewVendor, setShowNewVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [newVendorCode, setNewVendorCode] = useState("");

  // Match unmatched product dialog
  const [matchDialog, setMatchDialog] = useState({ open: false, unmatchedProduct: null });
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogResults, setCatalogResults] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Component-level vendor assignment for inhouse-only products
  const [expandedInhouse, setExpandedInhouse] = useState({});
  const [inhouseComponents, setInhouseComponents] = useState([]);

  // Fetch preview data + vendors
  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const [previewRes, vendorRes] = await Promise.all([
        axios.get(
          `${API}/po/${poId}/generate-bom/preview?bom_type=${bomType}`,
          authH()
        ),
        axios.get(`${API}/vendors`, authH()),
      ]);
      const data = previewRes.data;
      setPoNumber(data.po_number || "");
      setVendors(vendorRes.data.vendors || []);
      setUnmatched(data.unmatched || []);

      // Build product state with included flags
      const prods = (data.matched || []).map((p) => ({
        ...p,
        expanded: true,
        included: p.included !== false,
        locked: bomType === "vendor" && p.inhouse_only,
        components: (p.components || []).map((c) => ({
          ...c,
          included: c.included !== false,
        })),
      }));
      setProducts(prods);

      // Auto-expand all products
      const exp = {};
      prods.forEach((p) => {
        exp[p.product_code] = true;
      });
      setExpandedProducts(exp);
    } catch (err) {
      toast.error("Failed to load BOM preview");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [poId, bomType]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  // When bomType changes, update product included/locked states
  useEffect(() => {
    setProducts((prev) =>
      prev.map((p) => {
        const locked = bomType === "vendor" && p.inhouse_only;
        return {
          ...p,
          locked,
          included: locked ? false : p.included,
        };
      })
    );
    // Clear inhouse standalone components when switching to inhouse mode
    if (bomType === "inhouse") {
      setInhouseComponents([]);
      setExpandedInhouse({});
    }
  }, [bomType]);

  const toggleProduct = (code) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.product_code === code && !p.locked
          ? { ...p, included: !p.included }
          : p
      )
    );
  };

  const toggleComponent = (productCode, compId) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.product_code === productCode
          ? {
              ...p,
              components: p.components.map((c) =>
                c.id === compId ? { ...c, included: !c.included } : c
              ),
            }
          : p
      )
    );
  };

  const removeComponent = (productCode, compId) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.product_code === productCode
          ? {
              ...p,
              components: p.components.filter((c) => c.id !== compId),
            }
          : p
      )
    );
  };

  const toggleExpand = (code) => {
    setExpandedProducts((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const handleCreateVendor = async () => {
    if (!newVendorName.trim()) return;
    try {
      const res = await axios.post(
        `${API}/vendors`,
        { name: newVendorName.trim(), code: newVendorCode.trim() },
        authH()
      );
      const v = res.data;
      setVendors((prev) => [...prev, v]);
      setVendorId(v.id);
      setVendorName(v.name);
      setShowNewVendor(false);
      setNewVendorName("");
      setNewVendorCode("");
      toast.success(`Vendor "${v.name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create vendor");
    }
  };

  // ===== Match Unmatched Product =====
  const openMatchDialog = (unmatchedProduct) => {
    setMatchDialog({ open: true, unmatchedProduct });
    setCatalogSearch(unmatchedProduct.product_code || "");
    setCatalogResults([]);
    searchCatalog(unmatchedProduct.product_code || "");
  };

  const searchCatalog = async (query) => {
    setCatalogLoading(true);
    try {
      const res = await axios.get(
        `${API}/catalog/products${query ? `?search=${encodeURIComponent(query)}` : ""}`,
        authH()
      );
      setCatalogResults(res.data.products || []);
    } catch {
      setCatalogResults([]);
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleMatchProduct = async (catalogProduct) => {
    const um = matchDialog.unmatchedProduct;
    if (!um || !catalogProduct) return;

    try {
      // Fetch components for the catalog product
      const compRes = await axios.get(
        `${API}/catalog/products/${catalogProduct.id}/components`,
        authH()
      );
      const comps = (compRes.data.flat || []).map((c) => {
        const dimL = parseFloat(c.blank_size_l || 0);
        const dimW = parseFloat(c.blank_size_w || 0);
        const dimT = parseFloat(c.blank_size_t || 0);
        const qpu = parseFloat(c.qty_per_unit || 0);
        let wtPc = parseFloat(c.weight_per_piece || 0);
        if (dimL && dimW && dimT && !wtPc) wtPc = dimL * dimW * dimT * 0.00000786;
        const totalQty = qpu ? Math.round(qpu * um.qty_ordered) : um.qty_ordered;
        const wtAssly = wtPc ? wtPc * totalQty : 0;
        const areaPc = dimL && dimW ? 2 * (dimL * dimW) / (304.8 * 304.8) : 0;
        const totalArea = areaPc ? areaPc * totalQty : 0;
        return {
          ...c,
          total_qty: totalQty,
          wt_pc: wtPc || null,
          wt_assly: wtAssly || null,
          area_pc: areaPc || null,
          total_area: totalArea || null,
          included: true,
        };
      });

      const isInhouseOnly = !!catalogProduct.inhouse_only;
      const locked = bomType === "vendor" && isInhouseOnly;

      const newProduct = {
        product_code: um.product_code,
        description: catalogProduct.description,
        model_number: catalogProduct.product_code,
        rev_no: catalogProduct.rev_no || "",
        colour: catalogProduct.colour || "",
        remarks: catalogProduct.remarks || "",
        weight_mode: catalogProduct.weight_mode || "auto",
        weight_per_piece: catalogProduct.weight_per_piece,
        qty_ordered: um.qty_ordered,
        catalog_product_id: catalogProduct.id,
        inhouse_only: isInhouseOnly,
        included: !locked,
        locked,
        components: comps,
      };

      // Add to products, remove from unmatched
      setProducts((prev) => [...prev, newProduct]);
      setUnmatched((prev) => prev.filter((x) => x.product_code !== um.product_code));
      setExpandedProducts((prev) => ({ ...prev, [um.product_code]: true }));
      setMatchDialog({ open: false, unmatchedProduct: null });
      toast.success(`Matched ${um.product_code} → ${catalogProduct.product_code}`);
    } catch (err) {
      toast.error("Failed to fetch product components");
    }
  };

  // ===== Component-Level Vendor Assignment (Inhouse Products) =====
  const toggleExpandInhouse = async (productCode) => {
    const isOpen = expandedInhouse[productCode];
    setExpandedInhouse((prev) => ({ ...prev, [productCode]: !isOpen }));

    // Fetch components if not already loaded
    if (!isOpen) {
      const product = products.find((p) => p.product_code === productCode);
      if (product && product.components.length > 0) return; // Already have components
    }
  };

  const toggleInhouseComponent = (productCode, comp) => {
    const key = `${productCode}::${comp.id}`;
    const exists = inhouseComponents.find((c) => c._key === key);
    if (exists) {
      setInhouseComponents((prev) => prev.filter((c) => c._key !== key));
    } else {
      const product = products.find((p) => p.product_code === productCode);
      const qtyOrdered = product?.qty_ordered || 0;
      const dimL = parseFloat(comp.blank_size_l || 0);
      const dimW = parseFloat(comp.blank_size_w || 0);
      const dimT = parseFloat(comp.blank_size_t || 0);
      const qpu = parseFloat(comp.qty_per_unit || 0);
      let wtPc = parseFloat(comp.weight_per_piece || 0);
      if (dimL && dimW && dimT && !wtPc) wtPc = dimL * dimW * dimT * 0.00000786;
      const totalQty = qpu ? Math.round(qpu * qtyOrdered) : qtyOrdered;
      const wtAssly = wtPc ? wtPc * totalQty : 0;
      const areaPc = dimL && dimW ? 2 * (dimL * dimW) / (304.8 * 304.8) : 0;
      const totalArea = areaPc ? areaPc * totalQty : 0;

      setInhouseComponents((prev) => [
        ...prev,
        {
          _key: key,
          _parentCode: productCode,
          _parentDescription: product?.description || "",
          product_code: `${productCode}`,
          description: comp.description,
          model_number: comp.model_number || "",
          rev_no: product?.rev_no || "",
          colour: product?.colour || "",
          remarks: comp.remarks || "",
          qty_ordered: totalQty,
          weight_mode: "auto",
          included: true,
          components: [{
            ...comp,
            total_qty: totalQty,
            wt_pc: wtPc || null,
            wt_assly: wtAssly || null,
            area_pc: areaPc || null,
            total_area: totalArea || null,
            included: true,
          }],
        },
      ]);
    }
  };

  const handleGenerate = async () => {
    if (bomType === "vendor" && !vendorName) {
      toast.error("Please select a vendor for vendor BOM");
      return;
    }

    const includedProducts = products
      .filter((p) => p.included)
      .map((p) => ({
        ...p,
        components: p.components.filter((c) => c.included),
      }));

    // Add standalone components from inhouse-only products (vendor mode)
    const allProducts = [...includedProducts, ...inhouseComponents];

    if (allProducts.length === 0) {
      toast.error("No products selected");
      return;
    }

    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/po/${poId}/generate-bom`,
        {
          bom_type: bomType,
          vendor_id: vendorId,
          vendor_name: vendorName,
          dispatch_date: dispatchDate,
          products: allProducts,
        },
        authH()
      );
      toast.success(res.data.message || "BOM generated successfully!");
      nav("/safety/po");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate BOM");
    } finally {
      setGenerating(false);
    }
  };

  // Calculate totals for included items
  const totals = products
    .filter((p) => p.included)
    .reduce(
      (acc, p) => {
        acc.totalQty += p.qty_ordered || 0;
        const comps = p.components.filter((c) => c.included);
        comps.forEach((c) => {
          acc.totalWeight += c.wt_assly || 0;
          acc.totalArea += c.total_area || 0;
        });
        // For manual weight products (hardware packs)
        if (p.weight_mode === "manual" && p.weight_per_piece) {
          acc.totalWeight += p.weight_per_piece * (p.qty_ordered || 0);
        }
        return acc;
      },
      { totalQty: 0, totalWeight: 0, totalArea: 0 }
    );

  // Add inhouse standalone components to totals
  inhouseComponents.forEach((ic) => {
    totals.totalQty += ic.qty_ordered || 0;
    ic.components.forEach((c) => {
      totals.totalWeight += c.wt_assly || 0;
      totals.totalArea += c.total_area || 0;
    });
  });

  const includedCount = products.filter((p) => p.included).length + inhouseComponents.length;
  const poDisplay = poNumber
    ? poNumber.length < 7
      ? poNumber.padStart(7, "0")
      : poNumber
    : "";

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#0f1729" }}
        data-testid="bom-builder-loading"
      >
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
        <span className="ml-3 text-teal-300 text-lg">
          Loading BOM data...
        </span>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div
        className="min-h-screen flex flex-col"
        style={{ background: "#0f1729", color: "#e2e8f0" }}
        data-testid="bom-builder-page"
      >
        {/* ===== TOP BAR ===== */}
        <div
          className="flex items-center gap-4 px-6 py-3 border-b"
          style={{
            background: "#141e33",
            borderColor: "#1e3a5f",
          }}
          data-testid="bom-builder-topbar"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav("/safety/po")}
            className="text-slate-400 hover:text-white hover:bg-slate-700/50"
            data-testid="bom-builder-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>

          <Separator orientation="vertical" className="h-6 bg-slate-600" />

          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-teal-400" />
            <span className="font-semibold text-teal-100 tracking-tight">
              BOM Builder
            </span>
            <Badge
              variant="outline"
              className="text-xs border-teal-600 text-teal-300 ml-1"
            >
              PO {poDisplay}
            </Badge>
          </div>

          <div className="flex-1" />

          {/* BOM Type Toggle */}
          <Tabs
            value={bomType}
            onValueChange={(v) => {
              setBomType(v);
              if (v === "inhouse") {
                setVendorId("");
                setVendorName("");
              }
            }}
            data-testid="bom-type-tabs"
          >
            <TabsList className="bg-slate-800 border border-slate-600">
              <TabsTrigger
                value="inhouse"
                className="data-[state=active]:bg-teal-700 data-[state=active]:text-white text-slate-400 gap-1.5"
                data-testid="bom-type-inhouse"
              >
                <Factory className="h-3.5 w-3.5" /> Inhouse
              </TabsTrigger>
              <TabsTrigger
                value="vendor"
                className="data-[state=active]:bg-cyan-700 data-[state=active]:text-white text-slate-400 gap-1.5"
                data-testid="bom-type-vendor"
              >
                <Truck className="h-3.5 w-3.5" /> Vendor
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Vendor Selector (only when vendor type) */}
          {bomType === "vendor" && (
            <div className="flex items-center gap-2">
              <Select
                value={vendorId}
                onValueChange={(v) => {
                  if (v === "__new__") {
                    setShowNewVendor(true);
                    return;
                  }
                  setVendorId(v);
                  const found = vendors.find((x) => x.id === v);
                  setVendorName(found?.name || "");
                }}
              >
                <SelectTrigger
                  className="w-52 bg-slate-800 border-slate-600 text-slate-200"
                  data-testid="vendor-select"
                >
                  <SelectValue placeholder="Select Vendor" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  {vendors.map((v) => (
                    <SelectItem
                      key={v.id}
                      value={v.id}
                      className="text-slate-200 focus:bg-slate-700"
                    >
                      {v.name}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value="__new__"
                    className="text-teal-400 focus:bg-slate-700"
                  >
                    <span className="flex items-center gap-1">
                      <Plus className="h-3 w-3" /> Add New Vendor
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Dispatch Date */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <Input
              type="date"
              value={dispatchDate}
              onChange={(e) => setDispatchDate(e.target.value)}
              className="w-40 bg-slate-800 border-slate-600 text-slate-200 text-sm"
              data-testid="dispatch-date-input"
            />
          </div>

          <Separator orientation="vertical" className="h-6 bg-slate-600" />

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={generating || includedCount === 0}
            className="bg-teal-600 hover:bg-teal-500 text-white font-medium px-5"
            data-testid="generate-bom-btn"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {generating ? "Generating..." : "Generate BOM"}
          </Button>
        </div>

        {/* ===== MAIN CONTENT ===== */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT PANEL: Product Matching */}
          <div
            className="w-80 flex-shrink-0 border-r flex flex-col"
            style={{ borderColor: "#1e3a5f", background: "#111c2e" }}
            data-testid="product-matching-panel"
          >
            <div className="px-4 py-3 border-b" style={{ borderColor: "#1e3a5f" }}>
              <h3 className="text-sm font-semibold text-teal-300 tracking-wide uppercase">
                PO Products
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {includedCount} of {products.length} selected
                {unmatched.length > 0 && (
                  <span className="text-amber-400 ml-1">
                    | {unmatched.length} unmatched
                  </span>
                )}
              </p>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-3 space-y-1.5">
                {/* Matched products */}
                {products.map((p) => (
                  <div
                    key={p.product_code}
                    className={`rounded-md border p-3 transition-all ${
                      p.included
                        ? "border-teal-700/50 bg-teal-900/20"
                        : "border-slate-700/40 bg-slate-800/30 opacity-60"
                    }`}
                    data-testid={`product-card-${p.product_code}`}
                  >
                    <div className="flex items-center gap-2">
                      {p.locked ? (
                        <Tooltip>
                          <TooltipTrigger>
                            <Lock className="h-4 w-4 text-amber-500 flex-shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            className="bg-slate-800 text-slate-200 border-slate-600"
                          >
                            Inhouse Only — excluded from vendor BOMs
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Switch
                          checked={p.included}
                          onCheckedChange={() => toggleProduct(p.product_code)}
                          className="data-[state=checked]:bg-teal-600"
                          data-testid={`product-toggle-${p.product_code}`}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-mono font-semibold text-teal-200 truncate">
                            {p.product_code}
                          </span>
                          {p.inhouse_only && (
                            <Badge className="text-[10px] px-1 py-0 bg-amber-900/50 text-amber-300 border-amber-700 h-4">
                              Inhouse
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 truncate">
                          {p.description}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-xs font-mono text-slate-300">
                          {p.qty_ordered}
                        </span>
                        <p className="text-[10px] text-slate-500">qty</p>
                      </div>
                    </div>
                    <div className="mt-1.5 text-[10px] text-slate-500">
                      {p.components.length} component
                      {p.components.length !== 1 ? "s" : ""}
                      {p.rev_no && <span className="ml-2">Rev {p.rev_no}</span>}
                      {p.colour && <span className="ml-2">{p.colour}</span>}
                    </div>

                    {/* Component-level vendor assignment for locked inhouse products */}
                    {p.locked && bomType === "vendor" && p.components.length > 0 && (
                      <div className="mt-2">
                        <button
                          onClick={() => toggleExpandInhouse(p.product_code)}
                          className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-cyan-400 hover:text-cyan-300 bg-cyan-900/20 hover:bg-cyan-900/40 rounded py-1.5 transition-colors border border-cyan-800/30"
                          data-testid={`add-components-btn-${p.product_code}`}
                        >
                          {expandedInhouse[p.product_code] ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <Plus className="h-3 w-3" />
                          )}
                          {expandedInhouse[p.product_code] ? "Hide Components" : "Add Components to Vendor BOM"}
                        </button>
                        {expandedInhouse[p.product_code] && (
                          <div className="mt-2 space-y-1 pl-1">
                            {p.components
                              .filter((c) => !c.parent_component_id)
                              .map((comp) => {
                                const isSelected = inhouseComponents.some(
                                  (ic) => ic._key === `${p.product_code}::${comp.id}`
                                );
                                const qpu = parseFloat(comp.qty_per_unit || 0);
                                const calcQty = qpu ? Math.round(qpu * p.qty_ordered) : p.qty_ordered;
                                return (
                                  <label
                                    key={comp.id}
                                    className={`flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer transition-colors ${
                                      isSelected
                                        ? "bg-cyan-900/30 border border-cyan-700/40"
                                        : "hover:bg-slate-800/50 border border-transparent"
                                    }`}
                                    data-testid={`inhouse-comp-${comp.id}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleInhouseComponent(p.product_code, comp)}
                                      className="rounded border-slate-500 text-cyan-500 focus:ring-cyan-400 h-3.5 w-3.5"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <span className="text-xs text-slate-300">{comp.description}</span>
                                      <div className="text-[10px] text-slate-500">
                                        {comp.blank_size_l && `${comp.blank_size_l}×${comp.blank_size_w}×${comp.blank_size_t}`}
                                        {comp.material && <span className="ml-1">{comp.material}</span>}
                                        <span className="ml-1">Qty: {calcQty}</span>
                                      </div>
                                    </div>
                                  </label>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Unmatched products */}
                {unmatched.length > 0 && (
                  <>
                    <div className="pt-3 pb-1">
                      <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide">
                        Unmatched Products
                      </p>
                    </div>
                    {unmatched.map((u) => (
                      <div
                        key={u.product_code}
                        className="rounded-md border border-amber-800/40 bg-amber-900/10 p-3"
                        data-testid={`unmatched-card-${u.product_code}`}
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-mono text-amber-300">
                              {u.product_code}
                            </span>
                            <p className="text-xs text-slate-500 truncate">
                              {u.description}
                            </p>
                          </div>
                          <span className="text-xs text-slate-500">
                            {u.qty_ordered}
                          </span>
                        </div>
                        <button
                          onClick={() => openMatchDialog(u)}
                          className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-medium text-teal-400 hover:text-teal-300 bg-teal-900/20 hover:bg-teal-900/40 rounded py-1.5 transition-colors border border-teal-800/30"
                          data-testid={`match-btn-${u.product_code}`}
                        >
                          <Link className="h-3 w-3" />
                          Match to Catalog
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* RIGHT PANEL: BOM Preview Table */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Table header info */}
            <div
              className="px-6 py-3 flex items-center justify-between border-b"
              style={{ borderColor: "#1e3a5f", background: "#111c2e" }}
            >
              <div>
                <h3 className="text-sm font-semibold text-slate-200">
                  BOM Preview
                  {bomType === "vendor" && vendorName && (
                    <span className="text-cyan-400 ml-2">— {vendorName}</span>
                  )}
                </h3>
                <p className="text-xs text-slate-500">
                  {includedCount} products | Total Qty:{" "}
                  {totals.totalQty.toLocaleString()} | Weight:{" "}
                  {totals.totalWeight.toFixed(1)} Kgs | Area:{" "}
                  {totals.totalArea.toFixed(1)} Ft²
                </p>
              </div>
              <Badge
                variant="outline"
                className={`${
                  bomType === "inhouse"
                    ? "border-teal-600 text-teal-300"
                    : "border-cyan-600 text-cyan-300"
                }`}
              >
                {bomType === "inhouse" ? "Inhouse BOM" : "Vendor BOM"}
              </Badge>
            </div>

            {/* BOM Table */}
            <ScrollArea className="flex-1">
              <div className="px-4 py-2">
                <table className="w-full text-xs" data-testid="bom-preview-table">
                  <thead>
                    <tr
                      className="text-slate-400 uppercase tracking-wider"
                      style={{ background: "#182540" }}
                    >
                      <th className="text-left px-2 py-2 w-8">Sr.</th>
                      <th className="text-left px-2 py-2 min-w-[200px]">
                        Description
                      </th>
                      <th className="text-center px-2 py-2 w-24">Model</th>
                      <th className="text-center px-2 py-2 w-12">Rev</th>
                      <th className="text-center px-2 py-2 w-16">L</th>
                      <th className="text-center px-2 py-2 w-16">W</th>
                      <th className="text-center px-2 py-2 w-12">T</th>
                      <th className="text-center px-2 py-2 w-16">Material</th>
                      <th className="text-center px-2 py-2 w-14">Qty/U</th>
                      <th className="text-center px-2 py-2 w-16">Total Qty</th>
                      <th className="text-center px-2 py-2 w-16">Wt/pc</th>
                      <th className="text-center px-2 py-2 w-20">Wt/assly</th>
                      <th className="text-center px-2 py-2 w-16">Area</th>
                      <th className="text-center px-2 py-2 w-20">Total Area</th>
                      <th className="text-center px-2 py-2 w-24">Remarks</th>
                      <th className="text-center px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products
                      .filter((p) => p.included)
                      .map((product, idx) => (
                        <ProductRows
                          key={product.product_code}
                          product={product}
                          srNo={idx + 1}
                          expanded={expandedProducts[product.product_code]}
                          onToggleExpand={() =>
                            toggleExpand(product.product_code)
                          }
                          onToggleComponent={(compId) =>
                            toggleComponent(product.product_code, compId)
                          }
                          onRemoveComponent={(compId) =>
                            removeComponent(product.product_code, compId)
                          }
                        />
                      ))}
                    {/* Standalone components from inhouse-only products (vendor mode) */}
                    {inhouseComponents.map((ic, idx) => {
                      const baseSr = products.filter((p) => p.included).length;
                      return (
                        <ProductRows
                          key={ic._key}
                          product={ic}
                          srNo={baseSr + idx + 1}
                          expanded={true}
                          onToggleExpand={() => {}}
                          onToggleComponent={() => {}}
                          onRemoveComponent={() => {
                            setInhouseComponents((prev) =>
                              prev.filter((c) => c._key !== ic._key)
                            );
                          }}
                        />
                      );
                    })}
                  </tbody>
                </table>

                {includedCount === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-500">
                    <FileSpreadsheet className="h-12 w-12 mb-3 opacity-30" />
                    <p>No products selected</p>
                    <p className="text-xs mt-1">
                      Toggle products ON in the left panel to build the BOM
                    </p>
                  </div>
                )}

                {/* Summary row */}
                {includedCount > 0 && (
                  <div
                    className="mt-2 flex items-center justify-end gap-8 px-4 py-3 rounded border"
                    style={{
                      background: "#182540",
                      borderColor: "#1e3a5f",
                    }}
                    data-testid="bom-summary-row"
                  >
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase">
                        Products
                      </span>
                      <p className="text-sm font-mono text-teal-300">
                        {includedCount}
                      </p>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase">
                        Total Qty
                      </span>
                      <p className="text-sm font-mono text-teal-300">
                        {totals.totalQty.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase">
                        Total Weight
                      </span>
                      <p className="text-sm font-mono text-teal-300">
                        {totals.totalWeight.toFixed(2)} Kgs
                      </p>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase">
                        Total Area
                      </span>
                      <p className="text-sm font-mono text-teal-300">
                        {totals.totalArea.toFixed(2)} Ft²
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* New Vendor Dialog */}
        <Dialog open={showNewVendor} onOpenChange={setShowNewVendor}>
          <DialogContent className="bg-slate-800 border-slate-600 text-slate-200">
            <DialogHeader>
              <DialogTitle className="text-teal-300">
                Add New Vendor
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs text-slate-400">Vendor Name *</label>
                <Input
                  value={newVendorName}
                  onChange={(e) => setNewVendorName(e.target.value)}
                  placeholder="e.g. JAY INDUSTRIES"
                  className="bg-slate-900 border-slate-600 text-slate-200 mt-1"
                  data-testid="new-vendor-name-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">
                  Vendor Code (optional)
                </label>
                <Input
                  value={newVendorCode}
                  onChange={(e) => setNewVendorCode(e.target.value)}
                  placeholder="e.g. JAY"
                  className="bg-slate-900 border-slate-600 text-slate-200 mt-1"
                  data-testid="new-vendor-code-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setShowNewVendor(false)}
                className="text-slate-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateVendor}
                className="bg-teal-600 hover:bg-teal-500 text-white"
                data-testid="save-new-vendor-btn"
              >
                Create Vendor
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Match Product Dialog */}
        <Dialog open={matchDialog.open} onOpenChange={(open) => !open && setMatchDialog({ open: false, unmatchedProduct: null })}>
          <DialogContent className="bg-slate-800 border-slate-600 text-slate-200 max-w-lg max-h-[70vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-teal-300">
                Match "{matchDialog.unmatchedProduct?.product_code}" to Catalog
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-400">
              PO item: <span className="text-slate-300">{matchDialog.unmatchedProduct?.description}</span>
              {" | "}Qty: <span className="text-slate-300">{matchDialog.unmatchedProduct?.qty_ordered}</span>
            </p>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
              <Input
                value={catalogSearch}
                onChange={(e) => {
                  setCatalogSearch(e.target.value);
                  searchCatalog(e.target.value);
                }}
                placeholder="Search catalog by code or description..."
                className="bg-slate-900 border-slate-600 text-slate-200 pl-9"
                data-testid="match-catalog-search"
              />
            </div>
            <ScrollArea className="flex-1 min-h-0 mt-2">
              {catalogLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-teal-400" />
                </div>
              ) : catalogResults.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">No products found</p>
              ) : (
                <div className="space-y-1">
                  {catalogResults.map((cp) => (
                    <button
                      key={cp.id}
                      onClick={() => handleMatchProduct(cp)}
                      className="w-full text-left rounded-md border border-slate-700/50 bg-slate-900/50 px-3 py-2.5 hover:bg-teal-900/30 hover:border-teal-700/50 transition-colors"
                      data-testid={`match-option-${cp.product_code}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono font-semibold text-teal-200">
                          {cp.product_code}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {cp.inhouse_only && (
                            <Badge className="text-[9px] px-1 py-0 bg-amber-900/50 text-amber-300 border-amber-700 h-4">Inhouse</Badge>
                          )}
                          <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400 h-4">
                            {cp.component_count} comp
                          </Badge>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{cp.description}</p>
                      {cp.rev_no && <span className="text-[10px] text-slate-500">Rev {cp.rev_no}</span>}
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

/* ===== Color helper for remarks ===== */
const COLOR_MAP = {
  yellow: { bg: "#FFFF00", text: "#000" },
  red: { bg: "#FF0000", text: "#fff" },
  blue: { bg: "#0070C0", text: "#fff" },
  green: { bg: "#00B050", text: "#fff" },
  orange: { bg: "#FFC000", text: "#000" },
  black: { bg: "#000000", text: "#fff" },
  white: { bg: "#FFFFFF", text: "#000" },
};

function getRemarkStyle(remark) {
  if (!remark) return {};
  const lower = remark.trim().toLowerCase();
  if (COLOR_MAP[lower]) {
    return { backgroundColor: COLOR_MAP[lower].bg, color: COLOR_MAP[lower].text };
  }
  return {};
}

/* ===== Product Rows Sub-component ===== */
function ProductRows({
  product,
  srNo,
  expanded,
  onToggleExpand,
  onToggleComponent,
  onRemoveComponent,
}) {
  const rootComps = product.components.filter(
    (c) => !c.parent_component_id
  );

  const remarkVal = product.colour || product.remarks;
  const remarkStyle = getRemarkStyle(remarkVal);
  const revVal = product.rev_no;

  return (
    <>
      {/* Product header row — light grey background */}
      <tr
        className="border-t cursor-pointer hover:brightness-110 transition-colors"
        style={{ borderColor: "#1e3a5f", backgroundColor: "#1e2a3e" }}
        onClick={onToggleExpand}
        data-testid={`product-row-${product.product_code}`}
      >
        <td className="px-2 py-2 font-semibold text-teal-200">{srNo}</td>
        <td className="px-2 py-2">
          <div className="flex items-center gap-1.5">
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-teal-400" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
            )}
            <span className="font-semibold text-teal-100">
              {product.description}
            </span>
          </div>
        </td>
        <td className="px-2 py-2 text-center font-mono text-teal-200">
          {product.product_code}
        </td>
        <td
          className="px-2 py-2 text-center font-semibold"
          style={revVal ? { backgroundColor: "#FFFF00", color: "#000" } : {}}
        >
          {revVal}
        </td>
        <td colSpan={3}></td>
        <td></td>
        <td></td>
        <td className="px-2 py-2 text-center font-mono text-slate-200">
          {product.qty_ordered}
        </td>
        <td className="px-2 py-2 text-center font-mono text-slate-300">
          {product.weight_mode === "manual" && product.weight_per_piece
            ? product.weight_per_piece.toFixed(2)
            : ""}
        </td>
        <td className="px-2 py-2 text-center font-mono text-slate-300">
          {product.weight_mode === "manual" && product.weight_per_piece
            ? (product.weight_per_piece * product.qty_ordered).toFixed(1)
            : ""}
        </td>
        <td></td>
        <td></td>
        <td
          className="px-2 py-2 text-center text-xs font-medium"
          style={remarkStyle}
        >
          {remarkVal}
        </td>
        <td></td>
      </tr>

      {/* Component rows (when expanded) */}
      {expanded &&
        rootComps.map((comp) => (
          <ComponentRowGroup
            key={comp.id}
            comp={comp}
            allComponents={product.components}
            qtyOrdered={product.qty_ordered}
            onToggle={onToggleComponent}
            onRemove={onRemoveComponent}
            depth={0}
          />
        ))}
    </>
  );
}

/* ===== Component Row Group (recursive for children) ===== */
function ComponentRowGroup({
  comp,
  allComponents,
  qtyOrdered,
  onToggle,
  onRemove,
  depth,
}) {
  const children = allComponents.filter(
    (c) => c.parent_component_id === comp.id && c.included
  );

  return (
    <>
      <ComponentRow
        comp={comp}
        depth={depth}
        onToggle={onToggle}
        onRemove={onRemove}
      />
      {children.map((child) => (
        <ComponentRowGroup
          key={child.id}
          comp={child}
          allComponents={allComponents}
          qtyOrdered={qtyOrdered}
          onToggle={onToggle}
          onRemove={onRemove}
          depth={depth + 1}
        />
      ))}
    </>
  );
}

/* ===== Single Component Row ===== */
function ComponentRow({ comp, depth, onToggle, onRemove }) {
  const indent = depth * 16;
  const dimL = comp.blank_size_l;
  const dimW = comp.blank_size_w;
  const dimT = comp.blank_size_t;

  return (
    <tr
      className={`transition-colors ${
        comp.included
          ? "hover:bg-slate-800/50"
          : "opacity-40 line-through"
      }`}
      style={{ borderBottom: "1px solid #1a2a42" }}
      data-testid={`component-row-${comp.id}`}
    >
      <td className="px-2 py-1.5"></td>
      <td className="px-2 py-1.5" style={{ paddingLeft: `${8 + indent}px` }}>
        <span className="text-slate-300">{comp.description}</span>
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-400 text-[11px]">
        {comp.model_number}
      </td>
      <td className="px-2 py-1.5 text-center text-slate-500 text-[11px]">
        {comp.rev_no}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-400 text-[11px]">
        {dimL || ""}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-400 text-[11px]">
        {dimW || ""}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-400 text-[11px]">
        {dimT || ""}
      </td>
      <td className="px-2 py-1.5 text-center text-slate-400 text-[11px]">
        {comp.material}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-400 text-[11px]">
        {comp.qty_per_unit || ""}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-300 text-[11px]">
        {comp.total_qty || ""}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-400 text-[11px]">
        {comp.wt_pc ? comp.wt_pc.toFixed(4) : ""}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-300 text-[11px]">
        {comp.wt_assly ? comp.wt_assly.toFixed(2) : ""}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-400 text-[11px]">
        {comp.area_pc ? comp.area_pc.toFixed(4) : ""}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-slate-300 text-[11px]">
        {comp.total_area ? comp.total_area.toFixed(2) : ""}
      </td>
      <td
        className="px-2 py-1.5 text-center text-[11px] font-medium"
        style={getRemarkStyle(comp.remarks)}
      >
        {comp.remarks}
      </td>
      <td className="px-2 py-1.5 text-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(comp.id);
          }}
          className="text-slate-600 hover:text-red-400 transition-colors p-0.5"
          title="Remove component"
          data-testid={`remove-comp-${comp.id}`}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </td>
    </tr>
  );
}
